import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { WorkspacesState } from '../../store/workspaces/reducer';
import { tl } from '../../services/i18nService';
import * as styles from './Sidebar.scss';
import { toggleSideBar } from '../../store/workspaces/actions';
import CollapsibleSubMenu from './CollapsableSubMenu';
import { WorkspaceGroup } from '../../store/common/workspaceScheme/types';
import { CommonContextState } from '../../store/context/types';
import HideButton from '../HideButton/HideButton';
import { AWSRumCustomEvents } from '../../store/common/constants';
import { recordCustomEvent } from '../../util/awsRumUtils';

interface ComponentProps {
    mode: string;
    workspaces: WorkspacesState;
    availableWorkspaceGroups?: WorkspaceGroup[];
    context: CommonContextState;
    children?: any;
    collapse?: boolean;
    navigationCallback: (workspaceId: string) => void;
}

const CommonSidebar = memo((props: ComponentProps) => {
    const dispatch = useDispatch();
    const {
        workspaces,
        mode,
        children,
        navigationCallback,
    } = props;
    const expanded = children ? false : workspaces.sideBarExpanded;
    const toggleSidebar = () => {
        recordCustomEvent(AWSRumCustomEvents.TOGGLE_SIDEBAR, `Sidebar ${!expanded ? 'opened' : 'closed'}`);
        dispatch(toggleSideBar(!expanded));
    };

    const renderCollapsibleMenu = (): React.ReactNode => {
        const { availableWorkspaceGroups, context } = props;

        return availableWorkspaceGroups.map((group: WorkspaceGroup) => {
            return (
                <CollapsibleSubMenu
                    group={group}
                    context={context}
                    key={group.type}
                    navigationCallback={navigationCallback}
                />
            );
        });
    };

    const renderSidebarContent = useCallback(() => {
        if (children) {
            return children;
        }

        if (expanded) {
            return (
                <div>
                    <div className={styles.sidebarHeader}>
                        <h2 id="sidebarHeaderMode">{tl(`common.sidebars.${mode}`)}</h2>
                        <div className={styles.buttonContainer}>
                            <HideButton handleClick={toggleSidebar} inverted />
                        </div>
                    </div>
                    { renderCollapsibleMenu() }
                </div>
            );
        }

        return (
            <button
                type="button"
                onClick={toggleSidebar}
                title={tl('common.sidebar.toggleSidebar')}
                className={`${styles.maximizeSidebar}`}
                aria-expanded={false}
                aria-controls={mode}
            >
                <span>{tl(`common.sidebars.${mode}`)}</span>
            </button>
        );
    }, [workspaces, expanded, mode, children]);

    return (
        <div
            role="navigation"
            className={`${styles.sidebarWrapper} ${mode} ${expanded ? 'expanded' : ''}`}
            id={mode}
            aria-label={tl(`common.sidebars.${mode}`)}
        >
            {renderSidebarContent()}
        </div>
    );
});

export default CommonSidebar;
